import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ThemeProvider, useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

import Paper from "@mui/material/Paper";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import MenuItem from "@mui/material/MenuItem";

import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";

import Loading from "../Loading";
import { ButtonUI } from "../Common";
import TwoFAService from "../../service/TwoFAService";
import AuthService from "../../service/auth";
import { setUser, showToast } from "../../store/actions";

const useStyles = makeStyles()({
  paper: {
    marginTop: "108px",
    padding: "36px 16px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflowY: "auto",
    justifyContent: "center",
    textAlign: "center",
  },
  qrCodeContainer: {
    width: "100%",
    marginTop: "10px",
    marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  imageContainer: {
    padding: "8px;",
    border: "1px solid #ccc;",
    borderRadius: "5px;",
    backgroundColor: "#f9f9f9;",
    width: "230px;",
    height: "230px;",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1);",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
      backgroundColor: "#f1f1f1",
    },
    "& img": {
      width: "100%",
      height: "auto",
    },
  },
  mt12: {
    marginTop: "12px",
  },
});

const Set2FA = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const { classes } = useStyles();

  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [imageSrc, setImageSrc] = useState("");

  useEffect(() => {
    if (!user.currentUser) navigate("/signin");
    else {
      new TwoFAService().getQrCode().then((response) => {
        if (response.success) setImageSrc(response.data);
        setLoading(false);
      });
    }
  }, [user.currentUser, navigate, dispatch]);

  const logout = useCallback(() => {
    new AuthService().logout().then(() => {
      dispatch(setUser(null));
      navigate("/");
    });
  }, [dispatch, navigate]);

  const verify2FA = useCallback(() => {
    setLoading(true);
    new TwoFAService()
      .verify2FA(code)
      .then((response) => {
        if (response.success) {
          dispatch(
            showToast({
              message: response.message,
              type: "success",
            })
          );

          logout();
        }
      })
      .finally(() => setLoading(false));
  }, [code, dispatch, logout]);

  if (user.isLoading) return <Loading />;

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        sx={{
          color: "black",
          backgroundColor: "white",
          boxShadow: "0px 3px 40px #4343430A",
        }}
        position="absolute"
      >
        <Toolbar>
          <Typography component="h1" variant="h6" color="inherit" textAlign="left" noWrap sx={{ flexGrow: 1 }}>
            Two-Factor Authentication is Required
          </Typography>
          <MenuItem onClick={logout} data-id="signOut" sx={{ paddingY: "10px" }}>
            <LogoutOutlinedIcon sx={{ color: "#424242" }} /> <span style={{ marginLeft: "18px" }}>Sign Out</span>
          </MenuItem>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Container className={classes.paper} component={Paper} maxWidth="sm">
          <CssBaseline />
          <Box className={classes.container}>
            <Box>
              <Typography variant="body2">
                Download and install Google Authenticator/Microsoft Authenticator, or any other similar application to
                receive 2FA codes when prompted during the sign-in process.
              </Typography>
              <Typography variant="h6" className={classes.mt12}>
                Scan the QR code
              </Typography>
              <Typography variant="body2">Use an authenticator app on your phone to scan the QR code:</Typography>
              <Box className={classes.qrCodeContainer}>
                <Box className={classes.imageContainer}>
                  <img src={imageSrc} height="200px" width="200px" alt="QR Code" />
                </Box>
              </Box>
              <Typography variant="body2" className={classes.mt12}>
                Enter Verification Code:
              </Typography>
              <TextField
                margin="dense"
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                value={code}
                required
                inputProps={{ maxLength: 6 }}
              />
            </Box>
            <ButtonUI
              variant="contained"
              color="success"
              onClick={verify2FA}
              disabled={!code || loading}
              className={classes.mt12}
            >
              Submit
            </ButtonUI>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default React.memo(Set2FA);
